import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { Subscription } from "rxjs";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { ViewerConfigurationService } from "../../../services/viewer-configuration.service";
import { DualCommunicatorService } from "../../../services/internal-services/dual-communicator.service";
import { MeasurementToolService } from "../../../services/internal-services/measurement-tool.service";
import { ImagesService } from "../../../services/internal-services/images.service";
import { FeaturesSelectedService } from "../../../services/internal-services/features-selected.service";
import { ToastService } from "../../../services/internal-services/toast.service";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { LoadingService } from "../../../services/internal-services/loading.service";
import { PhoenixViewerService } from "../../../services/internal-services/viewer-services/phoenix-viewer.service";
import { BaseViewerContainerComponent } from "../base-viewer-container.component";
import { IHistoryProject } from "../../../interfaces/i-history-project";
import { Viewerlayer } from "../../../model/viewerlayer";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { ImageGalleryService } from "../../../services/internal-services/image-gallery/image-gallery.service";
import { MeasurementCommunicatorService } from "../../../../toolbars-components/services/measurement-communicator.service";
import { ProjectService } from "../../../services/internal-services/project.service";
import { IProjectAOI } from "../../../interfaces/i-project-aoi";
import { PhoenixViewerComponent } from "../../viewers/phoenix-viewer/phoenix-viewer.component";
import { CollectionManagerComponent } from "../../layer-managers/collection-manager/collection-manager.component";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { IVisionToast } from "../../../interfaces/i-vision-toast";
import { TOAST_STATUS } from "../../../model/ToastConstants";
import { MapService } from "../../../services/internal-services/map.service";
import { ReportService } from "../../../services/internal-services/report.service";
import { UrlStateService } from "../../../services/internal-services/url-state.service";
import { MapOverlaysService } from "../../../services/internal-services/overlay/map-overlays.service";
import { map } from "rxjs/operators";
import { DockGalleryComponent } from "../../../../dialog-components/image-gallery/dock-gallery/dock-gallery.component";
import { DomService } from "../../../services/internal-services/dom.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";
import { PropertiesLocationsManagerComponent } from "../../properties-locations-manager/properties-locations-manager.component";
import { ScreenshotComponent } from "../../screenshot/screenshot.component";
import { MapCanvasService } from "../../../services/internal-services/map-canvas.service";
import { OverlayRepositionService } from "../../../services/internal-services/overlay-reposition.service";
import { AgentDefinition } from "../../../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../../../ai-components/chat/model/tool-data.types";
import { DebugModeService } from "../../../../ai-components/chat/services/debug-mode.service";

/**
 * Enum for controller types
 */
enum ControllerType {
  COLLECTION = "collection",
  MAP = "map",
  COMPACT_MAP = "compact-map",
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-phoenix-viewer-container",
  templateUrl: "./phoenix-viewer-container.component.html",
  styleUrls: ["./phoenix-viewer-container.component.scss"],
  providers: [
    { provide: VIEWER_SERVICE, useClass: PhoenixViewerService },
    DebugModeService,
    ImageGalleryService,
    MeasurementToolService,
    ImagesService,
    FeaturesSelectedService,
    MapOverlaysService,
    ToastService,
    ToolbarService,
    LoadingService,
    MeasurementCommunicatorService,
    MapService,
    MapCanvasService,
    UrlStateService,
    ReportService,
    DomService,
    OverlayRepositionService,
  ],
  standalone: false,
})
export class PhoenixViewerContainerComponent
  extends BaseViewerContainerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public viewerService = inject(VIEWER_SERVICE);

  // Injected to force instantiation — MapCanvasService registers the screenshot
  // provider in its constructor so get_screenshot AI tool works without ScreenshotComponent.
  private readonly mapCanvas = inject(MapCanvasService);

  /** AI agents to display in the chat sidebar. Leave empty to hide the sidebar. */
  @Input() aiAgents: AgentDefinition[] = [];

  /**
   * Tool renderers for the AI chat sidebar.
   * Map tool names to Angular components. See ToolRendererConfig and
   * ToolCallDefaultRendererComponent for auto-detection of data types.
   */
  @Input() aiToolRenderers: ToolRendererConfig[] = [];

  @HostBinding("class.dual-child-container")
  @ViewChild(PhoenixViewerComponent)
  viewer: PhoenixViewerComponent;

  @ViewChild('fullscreenTpl', { read: TemplateRef })
  fullscreenTpl: TemplateRef<void>;

  @ViewChild(DockGalleryComponent)
  dockGallery: DockGalleryComponent;

  @ViewChild(CollectionManagerComponent)
  collectionManager: CollectionManagerComponent;

  @ViewChild(PropertiesLocationsManagerComponent)
  infoPropertiesManager: PropertiesLocationsManagerComponent;

  dualView = false;

  enableSync: boolean;

  allowSync: boolean;

  enableMeasurementTools = false;

  enableImageInfo = false;

  isToolbarOpen = true;

  enableSbsViewer = false; // side by side viewer

  resizeInfoMessage: boolean;

  private _projects: IHistoryProject[] = [];

  private _discreteProjects: IHistoryProject[] = [];

  private _collections: Viewerlayer[] = []; // visible collection with coverage at center

  private _sbsRightLayer: IProjectAOI = null;

  private _sbsLeftLayer: IProjectAOI = null;

  private resizeObserver: ResizeObserver;

  private fullscreenOverlayRef: OverlayRef | null = null;

  toolEnabled: string;

  showDevelopersTools = false;

  exportGeoTiffAllowed = false;

  // Active controller type (default: collection)
  activeController: ControllerType = ControllerType.COLLECTION;

  // Expose enum to template
  readonly ControllerType = ControllerType;

  constructor(
    mapService: MapService,
    reportService: ReportService,
    viewerConfigurationService: ViewerConfigurationService,
    public dualCommunicatorService: DualCommunicatorService,
    measurementCommunicatorService: MeasurementCommunicatorService,
    public toolbarService: ToolbarService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    galleryService: ImageGalleryService,
    toastService: ToastService,
    cdr: ChangeDetectorRef,
    flagService: FeatureFlagsService,
    private el: ElementRef,
    private overlayRepositionService: OverlayRepositionService,
    private cdkOverlay: Overlay,
    private vcr: ViewContainerRef,
  ) {
    super(
      mapService,
      reportService,
      viewerConfigurationService,
      galleryService,
      measurementCommunicatorService,
      toastService,
      cdr,
      flagService,
    );
  }

  ngOnInit() {
    this.mapService.mapIndex = this.mapIndex;
    this.subscriptions.push(
      this.galleryService.isFullscreenModeOn$.subscribe((isOn) => {
        if (isOn) {
          this.openFullscreenOverlay();
        } else {
          this.closeFullscreenOverlay();
        }
      }),
    );
    this.subscriptions.push(
      this.viewerService.developerTools$.subscribe((enable: boolean) => {
        this.showDevelopersTools = enable;
      }),
    );
    this.subscriptions.push(
      this.dualCommunicatorService.dualEnabled$.subscribe((enabled) => {
        this.dualView = enabled;
      }),
    );

    this.subscriptions.push(
      this.dualCommunicatorService.enableSync$.subscribe((enableSync) => {
        this.enableSync = enableSync;
      }),
    );

    this.subscriptions.push(
      this.dualCommunicatorService.allowSync$.subscribe((allowSync) => {
        this.allowSync = allowSync;
      }),
    );
    this.subscriptions.push(
      this.toolbarService.enabledTool$.subscribe((enabledTool) => {
        this.toolEnabled = enabledTool === "none" ? null : enabledTool;
        this.resizeInfoMessage =
          enabledTool !== "none" &&
          enabledTool !== "measurement_tool" &&
          enabledTool !== "drawing_tool" &&
          enabledTool !== "brightness_contrast_toolbar";
        this.viewerService.resizeInfoMessageSubject.next(this.resizeInfoMessage);
      }),
    );

    this.subscriptions.push(
      this.measurementCommunicatorService.measurementToolStatus$.subscribe((enable: boolean) => {
        this.enableMeasurementTools = enable;
      }),
    );

    this.subscriptions.push(
      this.viewerService.enableImageInfo$.subscribe((enable) => {
        this.enableImageInfo = enable;
      }),
    );
    this.subscriptions.push(
      this.viewerService.enableExportGeoTIFF$.subscribe((enable) => {
        this.exportGeoTiffAllowed = enable;
      }),
    );
  }

  ngAfterViewInit() {
    if (this.warningExpireInstance) {
      const notification: IVisionToast = {
        id: `toast-warning-expire`,
        message:
          "This instance will expire soon. Contact " +
          this.viewerConfigurationService.getViewerConfiguration().supportEmail +
          " to extend  the instance access.",
        closeButton: true,
        timeout: 30000,
        extendedTimeOut: 0, // Needed for avoid toast hide when hover
        positionClass: "toast-top-right",
        type: TOAST_STATUS.WARNING,
      };
      this.toastService.setNotification(notification);
    }

    if (this.warningExpireUser) {
      const notification: IVisionToast = {
        id: `toast-warning-expire`,
        message:
          "Your account access will expire soon. Contact " +
          this.viewerConfigurationService.getViewerConfiguration().supportEmail +
          " to extend your access",
        closeButton: true,
        timeout: 30000,
        extendedTimeOut: 0, // Needed for avoid toast hide when hover
        positionClass: "toast-top-right",
        type: TOAST_STATUS.WARNING,
      };
      this.toastService.setNotification(notification);
    }

    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.overlayRepositionService.updatePositions());
    });
    this.resizeObserver.observe(this.el.nativeElement);
  }

  /**
   * When receive new projects after movement
   * Calculate collections with coverage at this point
   * Add projects to each collection
   * @param projects
   */
  set projects(projects: IHistoryProject[]) {
    this._projects = projects;
  }

  get projects(): IHistoryProject[] {
    return this._projects;
  }

  set discreteProjects(projects: IHistoryProject[]) {
    this._discreteProjects = projects;
  }

  get discreteProjects(): IHistoryProject[] {
    return this._discreteProjects;
  }

  set collections(collections: Viewerlayer[]) {
    this._collections = collections;
  }

  get collections(): Viewerlayer[] {
    return this._collections;
  }

  set sbsRightLayer(aoi: IProjectAOI) {
    this._sbsRightLayer = aoi;
  }

  get sbsRightLayer(): IProjectAOI {
    return this._sbsRightLayer;
  }

  set sbsLeftLayer(aoi: IProjectAOI) {
    this._sbsLeftLayer = aoi;
  }

  get sbsLeftLayer(): IProjectAOI {
    return this._sbsLeftLayer;
  }

  private openFullscreenOverlay(): void {
    if (this.fullscreenOverlayRef) return;
    this.fullscreenOverlayRef = this.cdkOverlay.create({
      hasBackdrop: false,
      positionStrategy: this.cdkOverlay.position().global(),
      width: "100%",
      height: "100%",
      panelClass: "fullscreen-image-panel",
    });
    this.fullscreenOverlayRef.attach(new TemplatePortal(this.fullscreenTpl, this.vcr));
  }

  private closeFullscreenOverlay(): void {
    this.fullscreenOverlayRef?.dispose();
    this.fullscreenOverlayRef = null;
  }

  ngOnDestroy() {
    this.closeFullscreenOverlay();
    this.resizeObserver?.disconnect();
    super.ngOnDestroy();
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  updateVisibleCollections(collections: Viewerlayer[]) {
    this.collections = collections;

    const projects = this.viewerService.getLoadedProjects();
    if (projects) {
      this.updateCollectionsProjects(this.collections, projects);
    }
  }

  updateCollectionsProjects(collections: Viewerlayer[], projects: IHistoryProject[]) {
    if (projects?.length || collections?.length) {
      this.collections.map((collection) => {
        collection.projects = projects
          .filter((prj) => collection.layers.includes(prj.layer))
          .sort((p1, p2) => p2.maxDate.getTime() - p1.maxDate.getTime());
        collection.projects.map((prj) => {
          prj.layerName = collection.name;
          return prj;
        });
        return collection;
      });
      this.collections.map((collection) => {
        collection.aois = ProjectService.populateImageProjects(collection.aois, projects);

        return collection;
      });

      this.projects = [
        ...new Set(this.collections.reduce((acc, collection) => acc.concat(collection.projects), [])),
      ].sort((p1: IHistoryProject, p2: IHistoryProject) => p1.maxDate.getTime() - p2.maxDate.getTime());
      this.discreteProjects = [
        ...new Set(
          this.collections
            .filter((collection) => collection.enableOblique)
            .reduce((acc, collection) => acc.concat(collection.projects), []),
        ),
      ];
    }
  }

  showImageInfo(): boolean {
    return (
      this.viewerService.collectionMode.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name && this.enableImageInfo
    );
  }

  setMeasurementSystem(systemId: number) {
    this.viewerConfigurationService.setGlobalMeasurementSystem(systemId);
  }

  openToolbar() {
    if (this.dockGallery && this.dockGallery.displayPanel) {
      this.dockGallery.togglePanelDisplay();
    }
    if (this.collectionManager && this.collectionManager.displayPanel) {
      this.collectionManager.togglePanelDisplay();
    }
    if (this.infoPropertiesManager && this.infoPropertiesManager.displayPanel) {
      this.infoPropertiesManager.togglePanelDisplay();
    }
    this.isToolbarOpen = !this.isToolbarOpen;

    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.DUAL_VIEW_COLLAPSE_TOOLBAR,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  /**
   * Handle Alt+1 keyboard shortcut to toggle map-controller
   */
  @HostListener("window:keydown.alt.1", ["$event"])
  onAlt1KeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    this.activeController = ControllerType.MAP;
    this.cdr.markForCheck();
  }

  /**
   * Handle Alt+2 keyboard shortcut to toggle compact-map-controller
   */
  @HostListener("window:keydown.alt.2", ["$event"])
  onAlt2KeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    this.activeController = ControllerType.COMPACT_MAP;
    this.cdr.markForCheck();
  }

  /**
   * Handle Alt+3 keyboard shortcut to toggle collection-controller (default)
   */
  @HostListener("window:keydown.alt.3", ["$event"])
  onAlt3KeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    this.activeController = ControllerType.COLLECTION;
    this.cdr.markForCheck();
  }

  protected readonly map = map;
}
